import { useCallback, useEffect, useRef, useState } from "react";
import { Link, useLocation, useParams } from "react-router-dom";
import { Clock, Loader2, MapPin, Plane, Radio } from "lucide-react";
import api, { PLATFORM_NAME, errMsg } from "@/lib/api";
import { siblingPath, useRiderScope } from "@/lib/riderScope";
import { StageBadge } from "@/components/StageBadge";
import { ChatPanel } from "@/components/ChatPanel";
import { RouteMap } from "@/components/RouteMap";
import { LiveTripMap } from "@/components/LiveTripMap";

const STEPS = [
  ["driver_accepted", "Chauffeur assigned"],
  ["en_route_pickup", "On the way"],
  ["arrived_pickup", "Arrived at pickup"],
  ["onboard", "On board"],
  ["arrived_destination", "Dropped off"],
  ["completed", "Trip complete"],
];

const LIVE_MS = 10000;
const FULL_MS = 30000;

const headline = (live, eta, name) => {
  if (!live?.live) return { big: "—", small: "waiting for your chauffeur to set off" };
  if (live.at_pickup) return { big: "Here", small: `${name || "your chauffeur"} is at the pickup` };
  if (eta == null) return { big: "En route", small: "measuring the live drive time" };
  if (eta <= 1) return { big: "Arriving", small: `${name || "your chauffeur"} is pulling up now` };
  return {
    big: `${eta} min`,
    small: `until ${name || "your chauffeur"} reaches the ${live.heading_to === "dropoff" ? "destination" : "pickup"}`,
  };
};

export default function TrackTrip() {
  const { companyId, token } = useParams();
  const { pathname } = useLocation();
  const [data, setData] = useState(null);
  const [live, setLive] = useState(null);
  const [error, setError] = useState("");
  const [mapRoute, setMapRoute] = useState(null);
  const [tick, setTick] = useState(0);
  const stageRef = useRef("");
  const handle = data?.company?.handle || data?.company?.id || companyId;
  useRiderScope(handle, `track/${token}`);

  const load = useCallback(
    () => api.get(`/public/track/${token}`).then(({ data }) => {
      setData(data);
      stageRef.current = data.trip?.stage || "";
    }).catch((e) => setError(errMsg(e))),
    [token]
  );

  const pollLive = useCallback(
    () => api.get(`/public/track/${token}/live`).then(({ data }) => {
      setLive(data);
      // A stage change means the rest of the page is stale too.
      if (data.stage && stageRef.current && data.stage !== stageRef.current) load();
    }).catch(() => {}),
    [token, load]
  );

  useEffect(() => {
    load();
    pollLive();
    const fast = setInterval(pollLive, LIVE_MS);
    const slow = setInterval(load, FULL_MS);
    const clock = setInterval(() => setTick((t) => t + 1), 5000);
    return () => { clearInterval(fast); clearInterval(slow); clearInterval(clock); };
  }, [load, pollLive]);

  if (error) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-ink-900 p-6">
        <p data-testid="track-error" className="text-sm text-red-400">{error}</p>
      </div>
    );
  }
  if (!data) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-ink-900">
        <Loader2 className="h-5 w-5 animate-spin text-gold" />
      </div>
    );
  }

  const { trip: t, company: c } = data;
  const state = live?.stage ? live : t;
  const reachedIndex = STEPS.findIndex(([k]) => k === (state.stage || "driver_accepted"));
  const driver = state.live ? state.driver_location : null;
  const target = state.heading_to === "dropoff" ? t.dropoff : t.pickup;
  // The server ETA needs the unrestricted Google key; the drawn route gives us a drive time too.
  const eta = state.eta_minutes ?? (state.live ? mapRoute?.minutes : null) ?? null;
  const hero = headline(state, eta, t.chauffeur_name);
  const freshness = state.live && state.location_age_seconds != null
    ? `updated ${state.location_age_seconds < 60 ? `${state.location_age_seconds}s` : `${Math.round(state.location_age_seconds / 60)} min`} ago`
    : "";

  return (
    <div data-testid="track-page" className="min-h-screen bg-ink-900 px-4 py-10 text-slate-200" data-tick={tick}>
      <div className="mx-auto max-w-2xl space-y-6 rise">
        <div className="rounded-xl border border-ink-600 bg-ink-800 p-6" style={{ borderTop: `3px solid ${c.branding?.primary_color || "#D4AF37"}` }}>
          <p className="font-mono text-[11px] uppercase tracking-[0.28em] text-gold">{c.name}</p>
          <h1 className="mt-2 text-2xl font-bold text-slate-50">Trip #{t.number}</h1>
          <p className="mt-1 text-xs text-slate-500">
            {t.trip_date} · pickup {t.pickup_time}
            {t.original_pickup_time && t.original_pickup_time !== t.pickup_time
              ? ` (moved from ${t.original_pickup_time})` : ""}
          </p>
          <div className="mt-3 flex flex-wrap items-center gap-2">
            <StageBadge stage={state.stage || "assigned"} testId="track-stage" />
            {state.live && (
              <span data-testid="track-live-badge"
                className="flex items-center gap-1.5 rounded-full border border-emerald-500/40 bg-emerald-500/10 px-2.5 py-0.5 font-mono text-[10px] uppercase tracking-widest text-emerald-300">
                <Radio className="h-3 w-3 animate-pulse" /> Live
              </span>
            )}
          </div>
          <div className="mt-5 flex items-baseline gap-3">
            <Clock className="h-5 w-5 shrink-0 text-gold" />
            <p data-testid="track-eta" className="font-display text-3xl font-bold text-slate-50">{hero.big}</p>
            <p data-testid="track-eta-note" className="text-sm text-slate-400">{hero.small}</p>
          </div>
          {state.live && (
            <p data-testid="track-live-detail" className="mt-2 pl-8 font-mono text-[11px] text-slate-500">
              {state.miles_away != null ? `${state.miles_away} mi away · ` : ""}{freshness}
            </p>
          )}
        </div>

        {driver && target?.lat != null ? (
          <LiveTripMap driver={driver} target={target} testId="track-live-map"
            label={state.heading_to === "dropoff" ? "Destination" : "Pickup"} />
        ) : t.pickup?.lat != null && t.dropoff?.lat != null ? (
          <RouteMap points={[t.pickup, t.dropoff]} testId="track-map" onRoute={setMapRoute} />
        ) : null}

        {t.stage === "no_show" && (
          <div data-testid="track-no-show" className="rounded-xl border border-amber-400/40 bg-amber-400/10 p-6">
            <p className="text-sm font-semibold text-amber-200">Recorded as a no-show</p>
            <p className="mt-1.5 text-xs text-amber-100/80">
              Your chauffeur waited at the pickup and the trip was closed as a no-show. If this looks
              wrong, message {c.name} below and they can reverse it.
            </p>
          </div>
        )}

        <div className="rounded-xl border border-ink-600 bg-ink-800 p-6">
          <p className="font-mono text-[11px] uppercase tracking-widest text-gold">Progress</p>
          <div className="mt-4 space-y-2.5">
            {STEPS.map(([key, label], i) => (
              <div key={key} data-testid={`track-step-${key}`} className="flex items-center gap-3 text-sm">
                <span className={`h-2 w-2 rounded-full ${i <= reachedIndex ? "bg-gold" : "bg-ink-600"}`} />
                <span className={i <= reachedIndex ? "text-slate-100" : "text-slate-500"}>{label}</span>
                {(state.stage_at || t.stage_at)?.[key] && (
                  <span className="ml-auto font-mono text-[10px] text-slate-500">
                    {new Date((state.stage_at || t.stage_at)[key]).toLocaleTimeString()}
                  </span>
                )}
              </div>
            ))}
          </div>
        </div>

        <div className="space-y-2 rounded-xl border border-ink-600 bg-ink-800 p-6 text-sm text-slate-300">
          <p className="flex gap-2"><MapPin className="mt-0.5 h-4 w-4 shrink-0 text-gold" /> {t.pickup?.address || "—"}</p>
          {(t.stops || []).map((s, i) => (
            <p key={s.place_id || `${s.address}-${i}`} className="pl-6 text-xs text-slate-400">Stop {i + 1}: {s.address}</p>
          ))}
          <p className="flex gap-2"><MapPin className="mt-0.5 h-4 w-4 shrink-0 text-slate-500" /> {t.dropoff?.address || "—"}</p>
          <p className="pt-2 text-xs text-slate-500">
            {[t.vehicle_name, t.chauffeur_name].filter(Boolean).join(" · ") || "Vehicle to be assigned"}
          </p>
          {t.flight_status && (
            <p data-testid="track-flight" className="flex gap-2 pt-2 text-xs text-slate-300">
              <Plane className="mt-0.5 h-4 w-4 shrink-0 text-gold" />
              {t.flight_status.flight_number} · {t.flight_status.status || "scheduled"}
              {t.flight_delay_minutes ? ` · ${t.flight_delay_minutes} min late` : " · on time"}
            </p>
          )}
        </div>

        <ChatPanel
          listUrl={`/public/track/${token}/messages`}
          sendUrl={`/public/track/${token}/messages`}
          mine="passenger"
          title={`Message ${t.chauffeur_name || "your chauffeur"}`}
        />

        {["completed", "payment_finalized"].includes(state.stage) && (
          <Link to={siblingPath(pathname, handle, `receipt/${token}`)} data-testid="track-receipt-link"
            className="block rounded-xl border border-gold/30 bg-gold/5 p-5 text-center font-mono text-xs uppercase tracking-widest text-gold">
            View receipt, rate &amp; tip →
          </Link>
        )}

        {c.show_powered_by && (
          <p className="text-center font-mono text-[11px] text-slate-600">Powered by {PLATFORM_NAME}</p>
        )}
      </div>
    </div>
  );
}
